// remediationSnapshot.js — STABLE SNAPSHOT-BOUND REMEDIATION LAYER

import {
  getAuditSnapshotPayload,
  readRemediationContext,
  writeRemediationContext,
} from "./auditHistory";

// -------------------------
// Constants
// -------------------------

const REMEDIATION_MAP_KEY = "remediation_map";
const REMEDIATION_MAP_KEY_PREFIX = "aifine_remediation_map_";

// -------------------------
// Utilities
// -------------------------

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeStringify(obj) {
  try {
    return JSON.stringify(obj);
  } catch {
    return null;
  }
}

function readLS(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLS(key, val) {
  try {
    localStorage.setItem(key, val);
    return true;
  } catch {
    return false;
  }
}

function removeLS(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function asString(x) {
  return String(x || "").trim();
}

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return "";
  }
}

function safeObj(x) {
  return x && typeof x === "object" && !Array.isArray(x) ? x : {};
}

function ensureSnapshotId(snapshotId) {
  return asString(snapshotId);
}

function getSnapshotStorageKey(snapshotId) {
  const id = ensureSnapshotId(snapshotId);
  return id ? `${REMEDIATION_MAP_KEY_PREFIX}${id}` : REMEDIATION_MAP_KEY;
}

function looksLikeFindingStateMap(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const keys = Object.keys(obj);
  if (!keys.length) return false;

  return keys.every((key) => {
    if (["snapshotId", "snapshot_id", "map", "updated_at_iso"].includes(key)) return true;
    const value = obj[key];
    return value && typeof value === "object" && !Array.isArray(value);
  });
}

function readLegacyRemediationPayload() {
  return safeObj(safeParse(readLS(REMEDIATION_MAP_KEY), null));
}

function extractLegacySnapshotMap(snapshotId) {
  const sid = ensureSnapshotId(snapshotId);
  if (!sid) return {};

  const legacy = readLegacyRemediationPayload();
  if (!Object.keys(legacy).length) return {};

  if (legacy.snapshotId === sid && looksLikeFindingStateMap(legacy.map)) {
    return safeObj(legacy.map);
  }

  if (legacy.snapshot_id === sid && looksLikeFindingStateMap(legacy.map)) {
    return safeObj(legacy.map);
  }

  if (looksLikeFindingStateMap(legacy[sid])) {
    return safeObj(legacy[sid]);
  }

  // Earlier buggy writes sometimes stored the current snapshot's map directly in the
  // flat key. If so, recover it once rather than dropping the user's state.
  if (looksLikeFindingStateMap(legacy) && !legacy.map && !legacy.snapshotId && !legacy.snapshot_id) {
    return safeObj(legacy);
  }

  return {};
}

// -------------------------
// Public utility exports expected by RemediationHub
// -------------------------

export function cryptoRandomId(prefix = "id") {
  try {
    const cryptoObj =
      typeof globalThis !== "undefined" ? globalThis.crypto : undefined;

    if (cryptoObj?.randomUUID) {
      return `${prefix}_${cryptoObj.randomUUID()}`;
    }

    if (cryptoObj?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoObj.getRandomValues(bytes);
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${prefix}_${hex}`;
    }
  } catch {
    // fall through
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function downloadJson(filename, data) {
  try {
    const safeName = asString(filename) || "download.json";
    const payload = data ?? {};
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = safeName.endsWith(".json") ? safeName : `${safeName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore cleanup failure
      }
    }, 0);

    return true;
  } catch (err) {
    console.error("downloadJson failed", err);
    return false;
  }
}

// -------------------------
// Core Remediation Map
// -------------------------

export function readRemediationMap(options = undefined) {
  const snapshotId = ensureSnapshotId(options?.snapshotId);

  if (!snapshotId) {
    const raw = readLS(REMEDIATION_MAP_KEY);
    const parsed = safeParse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  }

  const scopedKey = getSnapshotStorageKey(snapshotId);
  const scopedRaw = readLS(scopedKey);
  const scopedParsed = safeParse(scopedRaw);
  if (scopedParsed && typeof scopedParsed === "object" && !Array.isArray(scopedParsed)) {
    return scopedParsed;
  }

  const recovered = extractLegacySnapshotMap(snapshotId);
  if (Object.keys(recovered).length) {
    writeLS(scopedKey, safeStringify(recovered));
    return recovered;
  }

  return {};
}

export function writeRemediationMap(arg1, arg2 = undefined) {
  // New preferred form:
  //   writeRemediationMap({ snapshotId, map })
  // Legacy form:
  //   writeRemediationMap(map)
  // Transitional convenience form:
  //   writeRemediationMap(snapshotId, map)

  if (typeof arg1 === "object" && arg1 !== null && (Object.prototype.hasOwnProperty.call(arg1, "snapshotId") || Object.prototype.hasOwnProperty.call(arg1, "snapshot_id"))) {
    const snapshotId = ensureSnapshotId(arg1.snapshotId || arg1.snapshot_id);
    const map = safeObj(arg1.map);
    if (!snapshotId) return false;
    const raw = safeStringify(map);
    if (!raw) return false;
    return writeLS(getSnapshotStorageKey(snapshotId), raw);
  }

  if (typeof arg1 === "string") {
    const snapshotId = ensureSnapshotId(arg1);
    const map = safeObj(arg2);
    if (!snapshotId) return false;
    const raw = safeStringify(map);
    if (!raw) return false;
    return writeLS(getSnapshotStorageKey(snapshotId), raw);
  }

  const raw = safeStringify(safeObj(arg1));
  if (!raw) return false;
  return writeLS(REMEDIATION_MAP_KEY, raw);
}

export function clearRemediationMap(snapshotId = "") {
  const sid = ensureSnapshotId(snapshotId);
  if (!sid) return removeLS(REMEDIATION_MAP_KEY);
  return removeLS(getSnapshotStorageKey(sid));
}

// -------------------------
// Snapshot Binding
// -------------------------

function getSnapshotPayload(snapshotId) {
  const id = ensureSnapshotId(snapshotId);
  if (!id) return null;
  return getAuditSnapshotPayload(id);
}

// -------------------------
// Canonical Remediation State
// -------------------------

export function getRemediationStateForSnapshot(snapshotId) {
  const id = ensureSnapshotId(snapshotId);
  if (!id) return {};

  const map = readRemediationMap({ snapshotId: id });

  return map && typeof map === "object" ? map : {};
}

export function setRemediationStateForSnapshot(snapshotId, state) {
  const id = ensureSnapshotId(snapshotId);
  if (!id) return false;

  const current = getRemediationStateForSnapshot(id);

  const next = {
    ...current,
    ...safeObj(state),
    updated_at_iso: nowIso(),
  };

  return writeRemediationMap({ snapshotId: id, map: next });
}

// -------------------------
// Finding-Level Helpers
// -------------------------

export function getFindingStatus(snapshotId, findingId) {
  const state = getRemediationStateForSnapshot(snapshotId);
  const fId = asString(findingId);

  if (!fId) return "OPEN";

  return state[fId]?.status || "OPEN";
}

export function setFindingStatus(snapshotId, findingId, status) {
  const id = ensureSnapshotId(snapshotId);
  const fId = asString(findingId);

  if (!id || !fId) return false;

  const state = getRemediationStateForSnapshot(id);

  state[fId] = {
    ...(state[fId] || {}),
    status: status || "OPEN",
    updated_at_iso: nowIso(),
  };

  return writeRemediationMap({ snapshotId: id, map: state });
}

// -------------------------
// Context Integration
// -------------------------

export function initializeRemediationContext(snapshotId) {
  const cleanSnapshotId = ensureSnapshotId(snapshotId);
  const payload = getSnapshotPayload(cleanSnapshotId);

  const context = {
    snapshot_id: cleanSnapshotId,
    entity_name: payload?.entity_name || payload?.summary?.entity_name || "Unknown",
    initialized_at_iso: nowIso(),
  };

  writeRemediationContext(context);

  return context;
}

export function getActiveRemediationContext() {
  const ctx = readRemediationContext();
  return ctx && typeof ctx === "object" ? ctx : null;
}

// -------------------------
// Safe Hydration Layer
// -------------------------

export function hydrateRemediationState(snapshotId) {
  const cleanSnapshotId = ensureSnapshotId(snapshotId);
  const payload = getSnapshotPayload(cleanSnapshotId);
  const state = getRemediationStateForSnapshot(cleanSnapshotId);

  return {
    snapshot_id: cleanSnapshotId,
    payload: payload || null,
    remediation: state || {},
  };
}
